import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveFocus, useData, useUI, inFocus } from '@/data/stores';
import {
  Badge,
  Button,
  Card,
  ConfidenceBar,
  Input,
  MetricCard,
  Modal,
  Select,
  StatusBadge,
  StatusDot,
  Table,
  type Column,
} from '@/components';
import { fmtNumber, fmtRelative } from '@/lib/format';
import { demoTimestamp, downloadCsv, downloadJson } from '@/lib/export';
import type { Asset, AssetType, Sector } from '@/types';
import { Search, Download, Plus } from 'lucide-react';
import './assets.css';

export function AssetsView() {
  const navigate = useNavigate();
  const allAssets = useData((s) => s.assets);
  const addAsset = useData((s) => s.addAsset);
  const notify = useUI((s) => s.notifyAction);
  const focus = useActiveFocus();
  const assets = useMemo(
    () => allAssets.filter((a) => inFocus(focus, a)),
    [allAssets, focus],
  );
  const [q, setQ] = useState('');
  const [sectorFilter, setSectorFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [critFilter, setCritFilter] = useState<string>('');
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    owner_operator: '',
    country: focus.country_codes?.find((c) => c !== 'XX') ?? 'US',
    sector: 'energy_transmission' as Sector,
    type: 'substation' as AssetType,
    criticality_rating: '4',
  });

  const sectors = useMemo(
    () => Array.from(new Set(assets.map((a) => a.sector))).sort(),
    [assets],
  );
  const countries = useMemo(
    () => Array.from(new Set(assets.map((a) => a.country))).sort(),
    [assets],
  );

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return assets.filter((a) => {
      if (sectorFilter && a.sector !== sectorFilter) return false;
      if (countryFilter && a.country !== countryFilter) return false;
      if (statusFilter && a.operational_status !== statusFilter) return false;
      if (critFilter && a.criticality_rating !== Number(critFilter)) return false;
      if (lower) {
        const blob = `${a.name} ${a.asset_id} ${a.owner_operator} ${a.tags.join(' ')}`.toLowerCase();
        if (!blob.includes(lower)) return false;
      }
      return true;
    });
  }, [assets, q, sectorFilter, countryFilter, statusFilter, critFilter]);

  const counts = useMemo(() => {
    const byStatus: Record<string, number> = {};
    const bySector: Record<string, number> = {};
    let critFive = 0;
    for (const a of filtered) {
      byStatus[a.operational_status] = (byStatus[a.operational_status] ?? 0) + 1;
      bySector[a.sector] = (bySector[a.sector] ?? 0) + 1;
      if (a.criticality_rating === 5) critFive += 1;
    }
    return {
      byStatus,
      bySector,
      critFive,
      total: filtered.length,
      avgCondition:
        filtered.length === 0
          ? 0
          : Math.round(filtered.reduce((s, a) => s + a.condition_score, 0) / filtered.length),
    };
  }, [filtered]);

  const columns: Column<Asset>[] = [
    {
      key: 'name',
      header: 'Asset',
      width: '24%',
      sortValue: (a) => a.name,
      render: (a) => (
        <div className="iris-assets-name-cell">
          <StatusDot status={a.operational_status} size={8} />
          <div>
            <div>{a.name}</div>
            <div className="caption mono">{a.asset_id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      width: '6%',
      mono: true,
      sortValue: (a) => a.country,
      render: (a) => a.country,
    },
    {
      key: 'sector',
      header: 'Sector',
      width: '14%',
      sortValue: (a) => a.sector,
      render: (a) => <span className="caption">{a.sector.replace(/_/g, ' ')}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      width: '12%',
      sortValue: (a) => a.type,
      render: (a) => <span className="caption">{a.type.replace(/_/g, ' ')}</span>,
    },
    {
      key: 'operator',
      header: 'Operator',
      width: '14%',
      sortValue: (a) => a.owner_operator,
      render: (a) => <span className="caption">{a.owner_operator}</span>,
    },
    {
      key: 'criticality',
      header: 'Crit',
      width: '5%',
      mono: true,
      align: 'right',
      sortValue: (a) => a.criticality_rating,
      render: (a) => `${a.criticality_rating}/5`,
    },
    {
      key: 'capacity',
      header: 'Capacity',
      width: '10%',
      mono: true,
      align: 'right',
      sortValue: (a) => a.capacity?.value ?? 0,
      render: (a) =>
        a.capacity ? `${fmtNumber(a.capacity.value)} ${a.capacity.unit}` : '-',
    },
    {
      key: 'condition',
      header: 'Condition',
      width: '8%',
      sortValue: (a) => a.condition_score,
      render: (a) => <ConfidenceBar value={a.condition_score} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '8%',
      sortValue: (a) => a.operational_status,
      render: (a) => <StatusBadge status={a.operational_status} />,
    },
    {
      key: 'lastTelemetry',
      header: 'Last seen',
      width: '8%',
      sortValue: (a) => a.last_telemetry_at ?? '',
      render: (a) =>
        a.last_telemetry_at ? (
          <span className="caption">{fmtRelative(a.last_telemetry_at)}</span>
        ) : (
          <span className="caption">-</span>
        ),
    },
  ];

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>Asset Registry & Digital Twins</h1>
          <p className="caption">
            Layer 1 - Every asset onboarded as a structured entity with semantic digital twin,
            interdependency graph, and compliance posture.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<Download size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadCsv(
                `iris-assets-${demoTimestamp()}.csv`,
                filtered.map((a) => ({
                  asset_id: a.asset_id,
                  name: a.name,
                  country: a.country,
                  sector: a.sector,
                  type: a.type,
                  operator: a.owner_operator,
                  criticality: a.criticality_rating,
                  status: a.operational_status,
                  condition: a.condition_score,
                  lat: a.coordinates.lat,
                  lon: a.coordinates.lon,
                })),
              );
              notify({
                kind: 'success',
                title: 'Asset export ready',
                body: `${filtered.length} filtered records downloaded as CSV.`,
              });
            }}
          >
            Export
          </Button>
          <Button
            variant="primary"
            icon={<Plus size={14} strokeWidth={1.5} />}
            onClick={() => setOnboardOpen(true)}
          >
            Onboard asset
          </Button>
        </div>
      </header>

      <Modal
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        title="Onboard Asset"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOnboardOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const now = new Date().toISOString();
                const cleanName = draft.name.trim() || 'Staged demo asset';
                const newAsset: Asset = {
                  asset_id: `AST-DEMO-${Date.now()}`,
                  name: cleanName,
                  type: draft.type,
                  sector: draft.sector,
                  owner_operator: draft.owner_operator.trim() || 'Manual intake',
                  country: draft.country.trim().toUpperCase() || 'US',
                  region: 'Staged intake',
                  coordinates: { lat: focus.center[1], lon: focus.center[0] },
                  criticality_rating: Number(draft.criticality_rating) as 1 | 2 | 3 | 4 | 5,
                  condition_score: 65,
                  operational_status: 'planned',
                  data_sources: ['Manual Intake', 'Demo Workflow'],
                  classification_level: 'official',
                  regulatory_framework: [],
                  tags: ['staged_intake', 'demo_onboarded', focus.id],
                  last_telemetry_at: now,
                };
                const regionId = focus.regions[0] ?? 'global';
                addAsset(newAsset, regionId);
                downloadJson(`iris-asset-intake-${newAsset.asset_id}-${demoTimestamp()}.json`, {
                  generated_at: now,
                  focus_mode: focus.id,
                  workflow: 'staged_asset_intake',
                  asset: newAsset,
                  next_steps: [
                    'Validate GIS location and geofence',
                    'Attach CMDB owner and telemetry sources',
                    'Approve compliance classification',
                  ],
                });
                setOnboardOpen(false);
                setDraft((cur) => ({ ...cur, name: '', owner_operator: '' }));
                notify({
                  kind: 'success',
                  title: 'Asset staged for onboarding',
                  body: `${newAsset.name} was added to the registry and an intake package was downloaded.`,
                });
              }}
            >
              Stage asset
            </Button>
          </>
        }
      >
        <div className="iris-scn-form">
          <Input
            label="Asset name"
            value={draft.name}
            onChange={(e) => setDraft((cur) => ({ ...cur, name: e.target.value }))}
            placeholder="e.g. West Loop 500kV Substation"
          />
          <Input
            label="Owner / operator"
            value={draft.owner_operator}
            onChange={(e) => setDraft((cur) => ({ ...cur, owner_operator: e.target.value }))}
            placeholder="Operator or owning organisation"
          />
          <Input
            label="Country"
            value={draft.country}
            onChange={(e) => setDraft((cur) => ({ ...cur, country: e.target.value }))}
            maxLength={2}
          />
          <Select
            label="Sector"
            value={draft.sector}
            onChange={(e) => setDraft((cur) => ({ ...cur, sector: e.target.value as Sector }))}
            options={ASSET_SECTORS.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
          <Select
            label="Type"
            value={draft.type}
            onChange={(e) => setDraft((cur) => ({ ...cur, type: e.target.value as AssetType }))}
            options={ASSET_TYPES.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
          <Select
            label="Criticality"
            value={draft.criticality_rating}
            onChange={(e) => setDraft((cur) => ({ ...cur, criticality_rating: e.target.value }))}
            options={[
              { value: '5', label: '5 - nationally critical' },
              { value: '4', label: '4 - high' },
              { value: '3', label: '3 - medium' },
              { value: '2', label: '2 - low' },
              { value: '1', label: '1 - minimal' },
            ]}
          />
        </div>
      </Modal>

      <section className="iris-page-metrics">
        <MetricCard
          label="Total assets"
          value={counts.total}
          caption="across selected filters"
        />
        <MetricCard
          label="Operational"
          value={counts.byStatus['operational'] ?? 0}
          status="var(--status-operational)"
        />
        <MetricCard
          label="Degraded"
          value={counts.byStatus['degraded'] ?? 0}
          status="var(--status-degraded)"
        />
        <MetricCard
          label="Offline / critical"
          value={counts.byStatus['offline'] ?? 0}
          status="var(--status-critical)"
        />
        <MetricCard
          label="Criticality 5"
          value={counts.critFive}
          caption="nationally critical"
        />
        <MetricCard
          label="Avg condition"
          value={counts.avgCondition}
          unit="/100"
        />
      </section>

      <Card
        title="Filter"
        subtitle="Narrow the registry by sector, country, status, criticality, or text"
      >
        <div className="iris-assets-filters">
          <Input
            label="Search"
            placeholder="Name, ID, operator, tag…"
            startIcon={<Search size={14} strokeWidth={1.5} />}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select
            label="Sector"
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            options={[
              { value: '', label: 'All sectors' },
              ...sectors.map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
            ]}
          />
          <Select
            label="Country"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            options={[
              { value: '', label: 'All countries' },
              ...countries.map((c) => ({ value: c, label: c })),
            ]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'operational', label: 'Operational' },
              { value: 'degraded', label: 'Degraded' },
              { value: 'offline', label: 'Offline' },
              { value: 'decommissioned', label: 'Decommissioned' },
              { value: 'under_construction', label: 'Under construction' },
              { value: 'planned', label: 'Planned' },
            ]}
          />
          <Select
            label="Criticality"
            value={critFilter}
            onChange={(e) => setCritFilter(e.target.value)}
            options={[
              { value: '', label: 'Any' },
              { value: '5', label: '5 - nationally critical' },
              { value: '4', label: '4 - high' },
              { value: '3', label: '3 - medium' },
              { value: '2', label: '2 - low' },
              { value: '1', label: '1 - minimal' },
            ]}
          />
          <div className="iris-assets-summary">
            <Badge color="var(--text-secondary)">
              {filtered.length} of {assets.length}
            </Badge>
          </div>
        </div>
      </Card>

      <div className="iris-page-table">
        <Table
          rows={filtered}
          columns={columns}
          rowKey={(a) => `${a.region_id}:${a.asset_id}`}
          onRowClick={(a) => navigate(`/assets/${a.asset_id}`)}
          defaultSort={{ key: 'criticality', dir: 'desc' }}
        />
      </div>
    </div>
  );
}

const ASSET_SECTORS: Sector[] = [
  'energy_generation',
  'energy_transmission',
  'energy_distribution',
  'oil_gas',
  'water',
  'wastewater',
  'maritime_ports',
  'telecommunications',
  'transport',
  'manufacturing',
  'data_centres',
  'defence',
  'government',
  'other',
];

const ASSET_TYPES: AssetType[] = [
  'power_plant',
  'substation',
  'transmission_line',
  'pipeline',
  'refinery',
  'port',
  'terminal',
  'water_treatment',
  'wastewater',
  'dam',
  'reservoir',
  'data_centre',
  'telecom_exchange',
  'manufacturing_facility',
  'solar_farm',
  'wind_farm',
  'hydroelectric',
  'nuclear',
  'lng_terminal',
  'rail_network',
  'airport',
  'military_installation',
  'government_facility',
  'submarine_cable',
  'other',
];
